import type { CtcaeVersion } from "../types";
import { onnxRuntimePlan, type OnnxRuntimePlan } from "./onnxRuntime";
import type {
  CatalogInput,
  EncoderModelSpec,
  ResolvedEncoderDtype,
  RuntimeAttempt,
  WorkerRequest,
  WorkerResponse,
} from "./types";

type Listener = (message: WorkerResponse) => void;

type ActiveRuntime = OnnxRuntimePlan & { dtype: ResolvedEncoderDtype };

type ConfigureAttempt = {
  requestId: number;
  model: EncoderModelSpec;
  startedAt: number;
  plan: OnnxRuntimePlan[];
  planIndex: number;
  runtime?: ActiveRuntime;
  failures: RuntimeAttempt[];
};

/** The timer is refreshed by download progress, so it bounds session setup and
 * readiness inference rather than the total time needed to fetch the model. */
export const RUNTIME_INITIALIZATION_TIMEOUT_MS = 15_000;

export class EncoderClient {
  private worker: Worker;
  private listeners = new Set<Listener>();
  private requestId = 0;
  private configured = false;
  private disposed = false;
  private configureAttempt?: ConfigureAttempt;
  private initializationTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.worker = this.createWorker();
  }

  private createWorker() {
    const worker = new Worker(new URL("./semantic.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      if (worker !== this.worker || this.disposed) return;
      this.handleWorkerMessage(event.data);
    };
    return worker;
  }

  private emit(message: WorkerResponse) {
    for (const listener of this.listeners) listener(message);
  }

  private clearInitializationTimer() {
    if (this.initializationTimer !== undefined) clearTimeout(this.initializationTimer);
    this.initializationTimer = undefined;
  }

  private armInitializationTimer() {
    const attempt = this.configureAttempt;
    if (!attempt?.runtime) return;
    this.clearInitializationTimer();
    this.initializationTimer = setTimeout(
      () => this.handleRuntimeFailure(attempt, this.timeoutReason(attempt.runtime!)),
      RUNTIME_INITIALIZATION_TIMEOUT_MS,
    );
  }

  private timeoutReason(runtime: ActiveRuntime) {
    const threads = runtime.wasmThreads === 0 ? "automatic threads" : `${runtime.wasmThreads} thread`;
    return `${runtime.device.toUpperCase()} initialization with ${threads} exceeded ${RUNTIME_INITIALIZATION_TIMEOUT_MS / 1_000} seconds.`;
  }

  private restartWorker() {
    this.clearInitializationTimer();
    this.worker.terminate();
    this.worker = this.createWorker();
  }

  private postRuntimeAttempt(attempt: ConfigureAttempt, invalidateModelCache: boolean) {
    const runtime = attempt.plan[attempt.planIndex];
    this.worker.postMessage({
      type: "configure",
      requestId: attempt.requestId,
      model: { ...attempt.model, device: runtime.device },
      invalidateModelCache,
      wasmThreads: runtime.wasmThreads,
    } satisfies WorkerRequest);
  }

  private handleRuntimeFailure(attempt: ConfigureAttempt, reason: string) {
    const runtime = attempt.runtime;
    if (this.configureAttempt !== attempt || !runtime || this.disposed) return;
    const elapsedMs = performance.now() - attempt.startedAt;
    console.debug("[CTCAE encoder] init:attempt-failed", {
      modelKey: attempt.model.key,
      device: runtime.device,
      dtype: runtime.dtype,
      wasmThreads: runtime.wasmThreads || "auto",
      reason,
      elapsedMs: Math.round(elapsedMs * 10) / 10,
    });
    attempt.failures.push({ ...runtime, status: "failed", message: reason });

    const next = attempt.plan[attempt.planIndex + 1];
    this.clearInitializationTimer();
    this.worker.terminate();
    if (!next) {
      this.configureAttempt = undefined;
      this.emit({
        type: "error",
        requestId: attempt.requestId,
        operation: "configure",
        message: `No encoder runtime passed initialization: ${reason}`,
      });
      return;
    }

    this.worker = this.createWorker();
    this.emit({
      type: "runtime-fallback",
      requestId: attempt.requestId,
      fromDevice: runtime.device,
      toDevice: next.device,
      fromWasmThreads: runtime.wasmThreads,
      toWasmThreads: next.wasmThreads,
      reason,
    });
    attempt.planIndex += 1;
    attempt.runtime = undefined;
    this.postRuntimeAttempt(attempt, false);
  }

  private handleWorkerMessage(message: WorkerResponse) {
    const attempt = this.configureAttempt;
    if (attempt && message.requestId === attempt.requestId) {
      if (message.type === "runtime-start") {
        attempt.runtime = {
          device: message.device,
          dtype: message.dtype,
          wasmThreads: message.wasmThreads,
        };
        this.armInitializationTimer();
      } else if (message.type === "model-progress" && attempt.runtime) {
        // Active download/cache progress is not a session-construction hang.
        this.armInitializationTimer();
      } else if (message.type === "model-ready") {
        this.clearInitializationTimer();
        this.configureAttempt = undefined;
        this.emit({
          ...message,
          loadMs: performance.now() - attempt.startedAt,
          runtimeAttempts: [...attempt.failures, ...message.runtimeAttempts],
        });
        return;
      } else if (message.type === "error" && message.operation === "configure") {
        this.clearInitializationTimer();
        if (attempt.runtime) {
          this.handleRuntimeFailure(attempt, message.message);
          return;
        }
        this.configureAttempt = undefined;
      }
    }
    this.emit(message);
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  configure(model: EncoderModelSpec, invalidateModelCache = false) {
    const requestId = ++this.requestId;
    if (this.configured) this.restartWorker();
    this.configured = true;
    const attempt: ConfigureAttempt = {
      requestId,
      model,
      startedAt: performance.now(),
      plan: onnxRuntimePlan(model.device),
      planIndex: 0,
      failures: [],
    };
    this.configureAttempt = attempt;
    this.postRuntimeAttempt(attempt, invalidateModelCache);
    return requestId;
  }

  index(version: CtcaeVersion, records: CatalogInput[], precomputed?: Extract<WorkerRequest, { type: "index" }>["precomputed"], forceRebuild = false) {
    const requestId = ++this.requestId;
    this.worker.postMessage({ type: "index", requestId, version, records, precomputed, forceRebuild } satisfies WorkerRequest);
    return requestId;
  }

  search(version: CtcaeVersion, query: string, limit = 16) {
    const requestId = ++this.requestId;
    this.worker.postMessage({ type: "search", requestId, version, query, limit } satisfies WorkerRequest);
    return requestId;
  }

  dispose() {
    const requestId = ++this.requestId;
    this.disposed = true;
    this.clearInitializationTimer();
    this.worker.postMessage({ type: "dispose", requestId } satisfies WorkerRequest);
    this.worker.terminate();
    this.listeners.clear();
  }
}
